import React, { useMemo } from "react";
import ClassNames from "classnames";
import { TextButton } from "../../controls";
import Tooltip from "../Tooltip/Tooltip";
import SeemPopover from "../SeemPopover/SeemPopover";
import { openManagementModesEnum } from "../SeemPopover/Popover";
import "./MultipleValuesDetailsItemPopover.scss";

export interface MultipleValuesDetailsItem {
  label: string;
  // Row text inside the "All (N)" popover; falls back to `label`.
  popoverLabel?: string;
}

export interface MultipleValuesDetailsItemPopoverProps {
  values: Array<string | MultipleValuesDetailsItem>;
  displayCount?: number;
  // Applied to the tooltips and the "All (N)" popover body, which render in a portal.
  className?: string;
}

const toItem = (value: string | MultipleValuesDetailsItem) =>
  typeof value === "string" ? { label: value } : value;

const MultipleValuesDetailsItemPopover: React.FC<
  MultipleValuesDetailsItemPopoverProps
> = ({ values, displayCount = 1, className }) => {
  const { items, visibleText, totalCount, shouldShowAll, hasPopoverLabels } =
    useMemo(() => {
      const safeItems = (values || []).map(toItem);
      return {
        items: safeItems,
        visibleText: safeItems
          .slice(0, displayCount)
          .map(({ label }) => label)
          .join(", "),
        totalCount: safeItems.length,
        shouldShowAll: safeItems.length > displayCount,
        hasPopoverLabels: safeItems.some(
          ({ popoverLabel }) => popoverLabel !== undefined
        ),
      };
    }, [values, displayCount]);

  return shouldShowAll ? (
    <SeemPopover
      openManagementMode={openManagementModesEnum.CLICK}
      body={
        <div
          className={ClassNames(
            "details-item-multiple-values-popover",
            className
          )}
        >
          <ul>
            {items.map(({ label, popoverLabel }, index) => {
              const line = popoverLabel ?? label;
              return (
                <li key={`${index}-${label}`}>
                  <Tooltip type="info" content={line} className={className}>
                    <span>{line}</span>
                  </Tooltip>
                </li>
              );
            })}
          </ul>
        </div>
      }
    >
      <span className={ClassNames("details-item-multiple-values", className)}>
        <span className="details-item-value">{visibleText}</span>
        <TextButton
          className="details-item-multiple-values__all-trigger"
          label={`All (${totalCount})`}
          buttonStyle="textLink"
          type="button"
        />
      </span>
    </SeemPopover>
  ) : (
    <Tooltip
      className={className}
      content={
        hasPopoverLabels
          ? items.map(({ label, popoverLabel }, index) => (
              <div key={`${index}-${label}`}>{popoverLabel ?? label}</div>
            ))
          : visibleText
      }
    >
      <span className="details-item-value">{visibleText}</span>
    </Tooltip>
  );
};

export default MultipleValuesDetailsItemPopover;
